import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { axios } from '../../libs/axios';
import logger from '../../utils/logger';

const PROJECT_TYPE_KEYS = [
  { key: 'general', labelKey: 'conversation.leadsAnalysis.projectTypeGeneral' },
  { key: 'hugoPlus', labelKey: 'conversation.leadsAnalysis.projectTypeHugoPlus' },
  { key: 'personal', labelKey: 'conversation.leadsAnalysis.projectTypePersonal' },
  { key: 'quote', labelKey: 'conversation.leadsAnalysis.projectTypeQuote' },
];

const REASON_KEYS = {
  eligible: 'conversation.leadsAnalysis.eligible',
  no_client: 'conversation.leadsAnalysis.noClient',
  has_paid_invoices: 'conversation.leadsAnalysis.hasPaidInvoices',
  has_project_general: 'conversation.leadsAnalysis.hasProjectGeneral',
  has_project_hugoPlus: 'conversation.leadsAnalysis.hasProjectHugoPlus',
  has_project_personal: 'conversation.leadsAnalysis.hasProjectPersonal',
  has_project_quote: 'conversation.leadsAnalysis.hasProjectQuote',
  not_found: 'conversation.leadsAnalysis.notFound',
  too_recent: 'conversation.leadsAnalysis.tooRecent',
};

const labelStyle = { marginBottom: '8px', fontWeight: '500' };

const defaultFilterState = () => ({
  addAll: false,
  excludePaidInvoice: true,
  projectTypes: {
    general: true,
    hugoPlus: true,
    personal: true,
    quote: true,
  },
});

function buildAddFilterPayload(filterState) {
  if (filterState.addAll) {
    return { addAll: true };
  }
  return {
    addAll: false,
    excludePaidInvoice: filterState.excludePaidInvoice,
    projectTypes: { ...filterState.projectTypes },
  };
}

function isFilterValid(filterState) {
  if (filterState.addAll) return true;
  if (filterState.excludePaidInvoice) return true;
  return PROJECT_TYPE_KEYS.some(({ key }) => filterState.projectTypes[key]);
}

export const GenerateLeadsModal = ({
  visible,
  onClose,
  conversationIds,
  chatbotId,
  sourceGraphVar,
  t,
}) => {
  const [phase, setPhase] = useState('setup');
  const [filterState, setFilterState] = useState(defaultFilterState);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [createNewList, setCreateNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [displayedLogCount, setDisplayedLogCount] = useState(0);
  const logTimerRef = useRef(null);

  const eligibleIds = useMemo(
    () => analysisResults.filter((r) => r.eligible).map((r) => r.conversationId),
    [analysisResults],
  );

  const addFilterPayload = useMemo(() => buildAddFilterPayload(filterState), [filterState]);
  const filterValid = useMemo(() => isFilterValid(filterState), [filterState]);

  const resetState = useCallback(() => {
    setPhase('setup');
    setFilterState(defaultFilterState());
    setAnalysisResults([]);
    setEligibleCount(0);
    setTotalCount(conversationIds.length);
    setCreateNewList(false);
    setNewListName('');
    setDisplayedLogCount(0);
    setConfirming(false);
  }, [conversationIds.length]);

  const fetchLists = useCallback(async () => {
    try {
      const response = await axios.get('/leads/lists');
      const nextLists = Array.isArray(response?.lists) ? response.lists : [];
      setLists(nextLists);
      setSelectedListId((prev) => {
        if (prev && nextLists.some((l) => String(l._id) === String(prev))) return prev;
        return nextLists[0]?._id ? String(nextLists[0]._id) : '';
      });
      if (!nextLists.length) {
        setCreateNewList(true);
      }
    } catch (error) {
      logger.error(error);
      setLists([]);
    }
  }, []);

  const runAnalysis = useCallback(async () => {
    if (!filterValid) {
      window.nError({ text: t('conversation.leadsAnalysis.filterRequired') });
      return;
    }

    setPhase('analyzing');
    setAnalysisResults([]);
    setDisplayedLogCount(0);
    setEligibleCount(0);
    setTotalCount(conversationIds.length);

    try {
      const response = await axios.post('/leads/analyze', {
        conversationIds,
        addFilter: addFilterPayload,
      });
      const results = Array.isArray(response?.results) ? response.results : [];
      setAnalysisResults(results);
      setEligibleCount(response?.eligibleCount || 0);
      setTotalCount(response?.total || results.length);
      setPhase('complete');
    } catch (error) {
      logger.error(error);
      window.nError({ text: error?.message || t('conversation.leadsAnalysis.error') });
      setPhase('setup');
    }
  }, [conversationIds, addFilterPayload, filterValid, t]);

  useEffect(() => {
    if (!visible) return undefined;
    resetState();
    fetchLists();
    return () => {
      if (logTimerRef.current) {
        clearInterval(logTimerRef.current);
      }
    };
  }, [visible, resetState, fetchLists]);

  useEffect(() => {
    if (phase !== 'complete' || !analysisResults.length) return undefined;

    if (logTimerRef.current) {
      clearInterval(logTimerRef.current);
    }

    setDisplayedLogCount(0);
    logTimerRef.current = setInterval(() => {
      setDisplayedLogCount((prev) => {
        if (prev >= analysisResults.length) {
          clearInterval(logTimerRef.current);
          return prev;
        }
        return prev + 1;
      });
    }, 60);

    return () => {
      if (logTimerRef.current) {
        clearInterval(logTimerRef.current);
      }
    };
  }, [phase, analysisResults]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = async () => {
    if (!eligibleIds.length) {
      window.nError({ text: t('conversation.leadsAnalysis.noEligible') });
      return;
    }

    let listId = selectedListId;
    try {
      setConfirming(true);

      if (createNewList) {
        const name = newListName.trim();
        if (!name) {
          window.nError({ text: t('leads.listNameRequired') });
          return;
        }
        const response = await axios.post('/leads/lists', { name });
        listId = response?.list?._id;
        if (!listId) {
          throw new Error(t('leads.listCreateError'));
        }
      } else if (!listId) {
        window.nError({ text: t('conversation.leadsAnalysis.selectList') });
        return;
      }

      const result = await axios.post(`/leads/lists/${listId}/entries`, {
        chatbotId,
        conversationIds: eligibleIds,
        sourceGraphVar,
        addFilter: addFilterPayload,
      });

      const added = result?.added || 0;
      const skipped = result?.skipped || 0;
      const alreadyLead = result?.alreadyLead || 0;
      const duplicates = result?.duplicates || 0;

      let successText = t('conversation.leadsAnalysis.addSuccessSummary', {
        added,
        skipped,
        alreadyLead,
        duplicates,
      });

      if (added === 0 && (skipped > 0 || alreadyLead > 0 || duplicates > 0)) {
        successText = t('conversation.leadsAnalysis.addNoneSummary', {
          skipped,
          alreadyLead,
          duplicates,
        });
      }

      window.nSuccess({ text: successText });
      handleClose();
    } catch (error) {
      window.nError({ text: error?.message || t('conversation.leadsAnalysis.addError') });
    } finally {
      setConfirming(false);
    }
  };

  if (!visible) return null;

  const visibleResults = phase === 'complete'
    ? analysisResults.slice(0, displayedLogCount)
    : [];

  return (
    <div className="modal prepayment generate-leads-modal" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modalclose" onClick={handleClose}>&times;</span>
          <h2>{t('conversation.generateLeads')}</h2>
        </div>
        <div className="modal-form d-flex flex-row flex-wrap">
          {phase === 'setup' && (
            <>
              <div className="form-row col-thin row-method leads-add-filter-panel">
                <div className="leads-add-filter-title">{t('conversation.leadsAnalysis.filterTitle')}</div>
                <p className="leads-add-filter-age-hint">{t('conversation.leadsAnalysis.ageHint')}</p>
                <label className="leads-add-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={filterState.addAll}
                    onChange={(e) => setFilterState((prev) => ({
                      ...prev,
                      addAll: e.target.checked,
                    }))}
                  />
                  <span>{t('conversation.leadsAnalysis.addAll')}</span>
                </label>
                {!filterState.addAll && (
                  <>
                    <p className="leads-add-filter-hint">
                      {t('conversation.leadsAnalysis.excludeHint')}
                    </p>
                    <fieldset className="leads-add-filter-fieldset">
                      <legend className="leads-add-filter-legend">
                        {t('conversation.leadsAnalysis.projectTypesLegend')}
                      </legend>
                      {PROJECT_TYPE_KEYS.map(({ key, labelKey }) => (
                        <label key={key} className="leads-add-filter-checkbox">
                          <input
                            type="checkbox"
                            checked={!!filterState.projectTypes[key]}
                            onChange={(e) => setFilterState((prev) => ({
                              ...prev,
                              projectTypes: {
                                ...prev.projectTypes,
                                [key]: e.target.checked,
                              },
                            }))}
                          />
                          <span>{t(labelKey)}</span>
                        </label>
                      ))}
                    </fieldset>
                    <label className="leads-add-filter-checkbox">
                      <input
                        type="checkbox"
                        checked={filterState.excludePaidInvoice}
                        onChange={(e) => setFilterState((prev) => ({
                          ...prev,
                          excludePaidInvoice: e.target.checked,
                        }))}
                      />
                      <span>{t('conversation.leadsAnalysis.excludePaidInvoice')}</span>
                    </label>
                  </>
                )}
              </div>
              <div className="form-row col-full row-actions">
                <button type="button" className="button button-large" onClick={handleClose}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className={`button button-large button-blue${!filterValid ? ' inactive' : ''}`}
                  disabled={!filterValid}
                  onClick={runAnalysis}
                >
                  {t('common.continue')}
                </button>
              </div>
            </>
          )}

          {phase === 'analyzing' && (
            <>
              <div className="form-row col-full row-desc">
                <p>{t('conversation.leadsAnalysis.analyzing', { count: totalCount || conversationIds.length })}</p>
              </div>
              <div className="form-row col-full row-actions">
                <button type="button" className="button button-large" disabled>
                  {t('common.cancel')}
                </button>
                <button type="button" className="button button-large button-blue" disabled>
                  {t('common.processing')}
                </button>
              </div>
            </>
          )}

          {phase === 'complete' && (
            <>
              <div className="form-row col-full row-label">
                <p className="generate-leads-summary">
                  {t('conversation.leadsAnalysis.summary', {
                    eligible: eligibleCount,
                    total: totalCount,
                  })}
                </p>
              </div>
              <div className="form-row col-thin row-method">
                <label style={labelStyle}>{t('conversation.leadsAnalysis.logTitle')}</label>
                <div className="analysis-log">
                  {visibleResults.map((row) => (
                    <div
                      key={row.conversationId}
                      className={`analysis-log-row analysis-log-row--${row.reason}`}
                    >
                      <span className="analysis-log-label">
                        {row.clientName || row.clientEmail || row.conversationId}
                      </span>
                      <span className="analysis-log-reason">
                        {t(REASON_KEYS[row.reason] || REASON_KEYS.not_found)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-row col-thin row-method">
                <label style={labelStyle}>{t('conversation.leadsAnalysis.listModeLabel')}</label>
                <select
                  value={createNewList ? '__new__' : selectedListId}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setCreateNewList(true);
                    } else {
                      setCreateNewList(false);
                      setSelectedListId(e.target.value);
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  {lists.map((list) => (
                    <option key={list._id} value={list._id}>
                      {list.name} ({list.entryCount || 0})
                    </option>
                  ))}
                  <option value="__new__">{t('conversation.leadsAnalysis.createNewList')}</option>
                </select>
              </div>
              {createNewList && (
                <div className="form-row col-thin row-method">
                  <label style={labelStyle}>{t('leads.listName')}</label>
                  <input
                    type="text"
                    value={newListName}
                    onChange={(e) => setNewListName(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
              )}
              <div className="form-row col-full row-actions">
                <button type="button" className="button button-large" onClick={handleClose}>
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className={`button button-large button-blue${eligibleCount === 0 ? ' inactive' : ''}`}
                  disabled={confirming || eligibleCount === 0}
                  onClick={handleConfirm}
                >
                  {confirming ? t('common.processing') : t('common.confirm')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
